import FindInPageIcon from '@mui/icons-material/FindInPage';
import MoreVert from '@mui/icons-material/MoreVert';
import {
  Box,
  Divider,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  lighten,
  useTheme,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import type {
  MRT_Row,
  MRT_TableInstance,
  MRT_TableOptions,
} from 'material-react-table';
import {
  MRT_GlobalFilterTextField,
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { useState } from 'react';

interface TableAction {
  title: string;
  onClick: () => void;
  icon?: React.ReactNode;
  disabled?: boolean;
}

interface TableActionGroup {
  groupTitle?: string;
  actions: TableAction[];
}

type DataAttributes = {
  [key: `data-${string}`]: string | number | boolean | undefined;
};

type TablePaperProps<T extends Record<string, any>> = Exclude<
  MRT_TableOptions<T>['muiTablePaperProps'],
  (props: { table: MRT_TableInstance<T> }) => unknown
>;

type SwitchTablePaperProps<T extends Record<string, any>> =
  | (TablePaperProps<T> & DataAttributes)
  | ((props: { table: MRT_TableInstance<T> }) => TablePaperProps<T>);

type Props<T extends Record<string, any>> = Omit<
  MRT_TableOptions<T>,
  'muiTablePaperProps'
> & {
  title: string;
  actions?: JSX.Element[];
  moreActions?: (TableAction | TableActionGroup)[];
  searchActions?: JSX.Element[];
  isError?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  onClick?: (row: MRT_Row<T>) => void;
  onDoubleClick?: (row: MRT_Row<T>) => void;
  disableDoubleClick?: (row: T) => boolean;
  enableTopToolbar?: boolean;
  enableBottomToolbar?: boolean;
  muiTablePaperProps?: SwitchTablePaperProps<T>;
  renderCustomTopToolbar?: (table: any) => JSX.Element;
};

const SwitchTable = <T extends Record<string, any>>({
  enableColumnResizing = false,
  enableRowActions = true,
  enableColumnActions = false,
  enableColumnFilters = true,
  positionActionsColumn = 'last' as const,
  enableStickyFooter = true,
  enableTopToolbar = true,
  enableBottomToolbar = true,
  enableColumnDragging = false,
  defaultColumn = {
    maxSize: 500,
    minSize: 40,
    size: 120,
  },
  enableStickyHeader = true,
  ...props
}: Props<T>) => {
  /* Hooks & Use Effects */
  const theme = useTheme();

  /* States */
  // No local state in main component

  /* API Calls */
  // No API calls in this component

  /* Functions & Functional Components */
  const ActionsSection = ({
    actions,
    moreActions,
  }: {
    actions?: JSX.Element[];
    moreActions?: (TableAction | TableActionGroup)[];
  }) => {
    /* Hooks & Use Effects */
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    /* States */
    const hasMoreActions = moreActions && moreActions.length > 0;

    /* API Calls */
    // No API calls in this component

    /* Functions & Functional Components */
    const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const renderMenuItem = (action: TableAction, key: string) => (
      <MenuItem
        key={key}
        onClick={() => {
          action.onClick();
          handleClose();
        }}
        disabled={action.disabled}
      >
        {action.icon && <ListItemIcon>{action.icon}</ListItemIcon>}
        <ListItemText>{action.title}</ListItemText>
      </MenuItem>
    );

    const renderMoreActions = () => {
      const items: JSX.Element[] = [];

      moreActions?.forEach((item, groupIndex) => {
        // Check if it's a group or individual action
        if ('actions' in item) {
          // It's a group
          const group = item as TableActionGroup;

          // Add divider before group (except for first item)
          if (groupIndex > 0) {
            items.push(<Divider key={`divider-${groupIndex}`} />);
          }

          // Add group header if provided
          if (group.groupTitle) {
            items.push(
              <ListSubheader key={`header-${groupIndex}`} component='div'>
                {group.groupTitle}
              </ListSubheader>
            );
          }

          // Add group actions
          group.actions.forEach((action, actionIndex) => {
            items.push(
              renderMenuItem(
                action,
                `group-${groupIndex}-action-${actionIndex}`
              )
            );
          });
        } else {
          // It's an individual action
          const action = item as TableAction;

          // Add divider before individual action (except for first item)
          if (groupIndex > 0) {
            items.push(<Divider key={`divider-${groupIndex}`} />);
          }

          items.push(renderMenuItem(action, `action-${groupIndex}`));
        }
      });

      return items;
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {/* Existing Actions */}
        {actions?.map((action, index) => (
          <Box key={`action-${index}`}>{action}</Box>
        ))}

        {/* Divider (only if we have both actions and more actions) */}
        {actions && actions.length > 0 && hasMoreActions && (
          <Divider
            orientation='vertical'
            flexItem
            sx={{
              mx: 1,
              borderRightWidth: 2,
              backgroundColor: theme.palette.divider,
            }}
          />
        )}

        {/* More Menu (only if moreActions exist) */}
        {hasMoreActions && (
          <>
            <Tooltip title='More actions'>
              <IconButton onClick={handleMoreClick}>
                <MoreVert />
              </IconButton>
            </Tooltip>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleClose}
              PaperProps={{
                sx: { minWidth: 180 },
              }}
            >
              {renderMoreActions()}
            </Menu>
          </>
        )}
      </Box>
    );
  };

  const table = useMaterialReactTable({
    ...props,
    data: props.data,
    columns: props.columns,
    enableTopToolbar: false,
    positionToolbarAlertBanner: 'bottom',
    columnFilterDisplayMode: 'popover',
    muiTablePaperProps: {
      ...props.muiTablePaperProps,
      elevation: 0,
      sx: {
        display: 'flex',
        flexDirection: 'column',
        background: 'transparent',
        width: '100%',
        flex: 1,
        maxHeight: '68vh',
        minHeight: '100%',
        ...(props.muiTablePaperProps && 'sx' in props.muiTablePaperProps
          ? props.muiTablePaperProps.sx
          : {}),
      },
    },
    muiTableContainerProps: {
      ...props.muiTableContainerProps,
      sx: {
        minHeight: 0,
        flexGrow: 1,
        display: 'flex',
        ...(props.muiTableContainerProps && 'sx' in props.muiTableContainerProps
          ? props.muiTableContainerProps.sx
          : {}),
      },
    },
    muiTableProps: {
      sx: {
        minHeight: 0,
        flexGrow: 1,
      },
      ...props.muiTableProps,
    },
    muiTableBodyProps: {
      ...props.muiTableBodyProps,
    },
    muiTableFooterProps: {
      ...props.muiTableFooterProps,
    },
    muiTableHeadRowProps: {
      sx: {
        backgroundColor: theme.colors.neutral.grey[20],
        '& > :first-of-type': {
          paddingLeft: 4,
        },
        '& > :last-of-type': {
          paddingRight: 4,
        },
      },
      ...props.muiTableHeadRowProps,
    },
    muiTableHeadProps: {
      sx: {
        backgroundColor: 'transparent !important',
        borderTop: `1px solid ${lighten(theme.colors.primary.main, 0.7)}`,
      },
      ...props.muiTableHeadProps,
    },
    enableTableFooter: props.enableTableFooter ?? false,
    mrtTheme: {
      ...props.mrtTheme,
    },
    muiBottomToolbarProps: {
      sx: {
        boxShadow: 'none',
        background: theme.colors.neutral.grey[20],
        borderTop: `1px solid ${theme.tokens.extendedColors.borders.light}`,
        paddingBottom: theme.tokens.spacing.xs * 1.45,
      },
      ...props.muiBottomToolbarProps,
    },
    muiPaginationProps: {
      rowsPerPageOptions: [10, 20, 30, 50, 100],
      ...props.muiPaginationProps,
    },
    muiToolbarAlertBannerProps: props.isError
      ? {
          color: 'error',
          children: 'Error loading data',
          sx: {
            border: 'none',
            mb: -6.5,
          },
        }
      : {
          sx: {
            mb: -6.5,
            border: 'none',
          },
          ...props.muiToolbarAlertBannerProps,
        },
    muiTableHeadCellProps: ({ column }) => ({
      sx: {
        py: 1.5,
        color: theme.colors.primary.main,
        boxShadow: column.getIsPinned() ? 'none' : undefined,
        pl: column.getIsPinned() ? 2 : undefined,
      },
    }),
    muiTableBodyCellProps: ({ column }) => ({
      sx: {
        boxShadow: column.getIsPinned() ? 'none' : undefined,
        pl: column.getIsPinned() ? 2 : undefined,
      },
    }),
    enableRowSelection: props.enableRowSelection,
    getRowId: (row) => row._id,
    enableColumnFilterModes: false,
    enableColumnDragging,
    enableColumnResizing,
    enableColumnPinning: true,
    layoutMode: 'semantic',
    displayColumnDefOptions: {
      'mrt-row-select': {
        size: 100,
        grow: false,
      },
      ...(enableRowActions && {
        'mrt-row-actions': {
          size: 100,
          grow: false,
        },
      }),
      ...props.displayColumnDefOptions,
    },
    manualFiltering: props.manualFiltering ?? true,
    onPaginationChange: props.onPaginationChange,
    onGlobalFilterChange: props.onGlobalFilterChange,
    onColumnFiltersChange: props.onColumnFiltersChange,
    onSortingChange: props.onSortingChange,
    enableGrouping: true,
    onRowSelectionChange: props.onRowSelectionChange,
    rowCount: props.rowCount,
    enableRowActions,
    positionActionsColumn,
    enableStickyHeader,
    enableColumnActions,
    enableColumnFilters,
    enableStickyFooter,
    enableBottomToolbar,
    defaultColumn,
    muiSelectAllCheckboxProps: {
      sx: { my: 0 },
    },
    state: {
      pagination: {
        pageIndex: 0,
        pageSize: 20,
      },
      showAlertBanner: props.isError,
      showProgressBars: props.isFetching,
      ...(enableRowActions && {
        columnPinning: {
          right: ['mrt-row-actions'],
          ...props.state?.columnPinning,
        },
      }),
      ...props.state,
    },
    initialState: {
      showColumnFilters: true,
      showGlobalFilter: props.onGlobalFilterChange !== undefined ? true : false,
      density: props.initialState?.density ?? 'compact',
      ...props.initialState,
    },
    muiTableBodyRowProps: (params) => {
      const { row } = params;
      const customRowProps =
        typeof props.muiTableBodyRowProps === 'function'
          ? props.muiTableBodyRowProps(params)
          : (props.muiTableBodyRowProps ?? {});
      const isDoubleClickDisabled =
        props.disableDoubleClick?.(row.original) ?? false;
      const hasDoubleClick = props.onDoubleClick && !isDoubleClickDisabled;
      const hasClick = props.onClick && !isDoubleClickDisabled;
      const hasInteraction = hasClick || hasDoubleClick;

      return {
        ...customRowProps,
        onClick: (event) => {
          customRowProps.onClick?.(event);
          if (hasClick) props.onClick!(row);
        },
        onDoubleClick: (event) => {
          customRowProps.onDoubleClick?.(event);
          if (hasDoubleClick) props.onDoubleClick!(row);
        },
        ...(hasInteraction
          ? {
              sx: {
                '& > :first-of-type': {
                  paddingLeft: 4,
                },
                '&:last-of-type > td, &:last-of-type > th': {
                  borderBottom: 0,
                },
                cursor: 'pointer',
                ...(row.getIsSelected() && {
                  backgroundColor: theme.palette.primary.light,
                  '&:hover': {
                    backgroundColor: theme.palette.background.paper,
                  },
                }),
                ...customRowProps.sx,
              },
            }
          : {
              sx: {
                '& > :first-of-type': {
                  paddingLeft: 4,
                },
                '&:last-of-type > td, &:last-of-type > th': {
                  borderBottom: 0,
                },
                ...customRowProps.sx,
              },
            }),
      };
    },
    renderEmptyRowsFallback: () => {
      if (props.isLoading) {
        return (
          <Grid
            container
            justifyContent='center'
            alignItems='center'
            padding={5}
            sx={{
              backgroundColor: theme.tokens.colors.background,
            }}
          >
            <Typography variant='h6'>
              Loading {props.title?.toLowerCase()}...
            </Typography>
          </Grid>
        );
      }

      return (
        <Grid
          container
          direction='column'
          justifyContent='center'
          alignItems='center'
          padding={5}
          sx={{
            backgroundColor: theme.tokens.colors.background,
          }}
        >
          <FindInPageIcon color={'warning'} fontSize='large' sx={{ mb: 1 }} />
          <Typography variant='h6'>
            No {props.title?.toLowerCase()} found
          </Typography>
        </Grid>
      );
    },
  });

  const topToolbarProps =
    typeof props.muiTopToolbarProps === 'function'
      ? props.muiTopToolbarProps({ table })
      : props.muiTopToolbarProps;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { sx: _topToolbarSx, ...topToolbarContainerProps } =
    topToolbarProps ?? {};

  const topToolbarSx = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    pt: 0,
    pb: '8px',
    minHeight: 48,
    overflowX: {
      xs: 'scroll',
      sm: 'scroll',
      md: 'auto',
    },
    overflowY: 'hidden',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
    '&::-webkit-scrollbar': {
      height: '6px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.grey[400],
      borderRadius: '4px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: theme.palette.grey[200],
    },
  };

  const renderTopToolbarContent = () => {
    if (props.renderCustomTopToolbar) {
      return props.renderCustomTopToolbar(table);
    }

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexShrink: 0,
            width: {
              xs: '150px',
              md: '450px',
            },
          }}
        >
          {props.searchActions ?? <MRT_GlobalFilterTextField table={table} />}
        </Box>

        <ActionsSection
          actions={props.actions}
          moreActions={props.moreActions}
        />
      </>
    );
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
        }}
      >
        {enableTopToolbar && (
          <Box {...topToolbarContainerProps} sx={topToolbarSx}>
            {renderTopToolbarContent()}
          </Box>
        )}

        <MaterialReactTable table={table} />
      </Box>
    </LocalizationProvider>
  );
};

export default SwitchTable;
